import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getPatientProductTypes,
  consumeProduct,
  deleteProduct,
  returnProduct,
} from '@/services/patientProductTypes.service';
import {
  ProductTypesDisplay,
  resolveTypeAndId,
  type ProductTypesResponse,
  type ActionHandler,
  type ProductItem,
} from '@/components/patients/PatientProductsDisplay';

export default function PatientProductTypesDebugPage() {
  const [patientId, setPatientId] = useState('');
  const [availableDate, setAvailableDate] = useState('');
  const [submittedId, setSubmittedId] = useState('');
  const [jsonOpen, setJsonOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['patientProductTypes', submittedId, availableDate],
    queryFn: () =>
      getPatientProductTypes(submittedId, availableDate ? { availableDate } : undefined),
    enabled: !!submittedId,
  });

  const handleAction: ActionHandler = useCallback(
    async (action: 'consume' | 'delete' | 'return', item: ProductItem, quantity?: number) => {
      const resolved = resolveTypeAndId(item);
      if (!resolved) {
        alert('Cannot determine product source type');
        return;
      }

      try {
        if (action === 'consume') {
          await consumeProduct(submittedId, { ...resolved, quantity });
        } else if (action === 'delete') {
          await deleteProduct(submittedId, { ...resolved, quantity });
        } else if (action === 'return') {
          await returnProduct(submittedId, {
            ...resolved,
            scheduledAppointmentProductsID: item.scheduledAppointmentProductsID ?? undefined,
          });
        }
        refetch();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Action failed';
        alert(msg);
      }
    },
    [submittedId, refetch],
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (patientId.trim()) {
      setSubmittedId(patientId.trim());
    }
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h1 className="mb-6 text-2xl font-bold">Patient Product Types — Debug</h1>

      <form onSubmit={handleSubmit} className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="patientId" className="mb-1 block text-sm font-medium">
            Patient ID
          </label>
          <input
            id="patientId"
            type="text"
            value={patientId}
            onChange={(e) => setPatientId(e.target.value)}
            className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="e.g. 12345"
          />
        </div>

        <div>
          <label htmlFor="availableDate" className="mb-1 block text-sm font-medium">
            Available Date (optional)
          </label>
          <input
            id="availableDate"
            type="datetime-local"
            value={availableDate}
            onChange={(e) => setAvailableDate(e.target.value)}
            className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <button
          type="submit"
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Load
        </button>

        {submittedId && (
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50"
          >
            Refresh
          </button>
        )}
      </form>

      {isLoading && <p className="text-gray-500">Loading…</p>}

      {error && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {data && (
        <>
          <div className="mb-6">
            <h2 className="mb-3 text-lg font-semibold">Products</h2>
            <ProductTypesDisplay data={data as ProductTypesResponse} onAction={handleAction} />
          </div>

          <div className="rounded border border-gray-200">
            <button
              type="button"
              onClick={() => setJsonOpen(!jsonOpen)}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              <span className={`text-xs transition-transform ${jsonOpen ? 'rotate-90' : ''}`}>
                &#9654;
              </span>
              Raw JSON Response
            </button>
            {jsonOpen && (
              <pre className="max-h-[70vh] overflow-auto border-t bg-gray-50 p-4 text-xs leading-relaxed">
                {JSON.stringify(data, null, 2)}
              </pre>
            )}
          </div>
        </>
      )}
    </div>
  );
}
